import { useEffect, useRef, useState } from "react";
import { io } from "socket.io-client";
import Peer from "simple-peer";
import { StreamVideo } from "../StreamVideo";
import { DevicePermissions } from "../DevicePermissions";

const socket = io(process.env.REACT_APP_API_URL, {
  transports: ["websocket"],
  reconnection: true,
  reconnectionAttempts: 20,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
});

const RTC_CONFIG = {
  iceServers: [
    { urls: ["stun:stun.l.google.com:19302", "stun:global.stun.twilio.com:3478"] },
    {
      urls: [
        "turn:stun.evan-brass.net",
        "turn:stun.evan-brass.net?transport=tcp",
        "turn:stun.evan-brass.net?transport=udp",
      ],
      username: "guest",
      credential: "password",
    },
  ],
};

export const useCallSocket = ({ user, videoOff, mute }) => {
  const connectionRef = useRef(null);
  const activeStreamRef = useRef(null);
  const remoteVideo = useRef(null);
  const Permissions = useRef(null);
  const [incomingCall, setIncomingCall] = useState(null);

  /* ---------------- Permissions ---------------- */

  useEffect(() => {
    Permissions.current = DevicePermissions(navigator.permissions);
  }, []);

  /* ---------------- Register Socket ---------------- */

  useEffect(() => {
    if (!user) return;

    socket.emit("join-room", {
      userId: user._id,
      userName: user.name,
      socketId: socket.id,
    });

    socket.on("calluser", ({ from, name, signal }) => {
      console.log("📞 Incoming call from", name);
      setIncomingCall({ from, name, signal });
    });

    socket.on("callended", cleanup);
    socket.on("userleft", cleanup);

    return () => {
      socket.off("calluser");
      socket.off("callended");
      socket.off("userleft");
    };
  }, [user]);

  /* ---------------- Accept Call (Sender) ---------------- */

  const acceptCall = async () => {
    if (!incomingCall) return;

    activeStreamRef.current = await navigator.mediaDevices.getUserMedia({
      video: !videoOff,
      audio: !mute,
    });

    const peer = new Peer({
      initiator: false,
      trickle: false,
      stream: activeStreamRef.current,
      config: RTC_CONFIG,
    });

    peer.on("signal", signal => {
      socket.emit("answercall", {
        to: incomingCall.from,
        signal,
      });
    });

    peer.on("close", stopCall);
    peer.on("error", console.error);

    peer.signal(incomingCall.signal);
    connectionRef.current = peer;
    setIncomingCall(null);
  };

  /* ---------------- Call User (Monitor / Receiver) ---------------- */

  const callUser = socketId => {
    const peer = new Peer({
      initiator: true,
      trickle: false,
      config: RTC_CONFIG,
      offerOptions: {
        offerToReceiveVideo: true,
        offerToReceiveAudio: true,
      },
    });

    peer.on("signal", signal => {
      socket.emit("calluser", {
        userToCall: socketId,
        signalData: signal,
        from: socket.id,
        name: user.name,
      });
    });

    peer.on("stream", stream => {
      console.log("🎥 Remote stream received");
      if (remoteVideo.current) {
        remoteVideo.current.srcObject = stream;
      }
    });

    socket.once("callaccepted", signal => {
      if (!peer.destroyed) peer.signal(signal);
    });

    peer.on("close", stopCall);
    peer.on("error", console.error);

    connectionRef.current = peer;
  };

  /* ---------------- Cleanup ---------------- */

  const cleanup = () => {
    connectionRef.current?.destroy();
    connectionRef.current = null;
    setIncomingCall(null);
    StreamVideo({ on: false });
  };

  const stopCall = () => cleanup();

  return {
    remoteVideo,
    callUser,
    stopCall,
    incomingCall,
    acceptCall,
  };
};
